'use client';

import { BRAND, BRAND_ENUM } from '@/lib/cms/config';
import { useEffect, useRef, useState } from 'react';

interface UseEntryEffectOptions {
  /** Whether the entry effect is enabled. When false, isVisible is always true. */
  enabled?: boolean;
  /**
   * Scroll container to observe against. When provided, the trigger line and
   * overlap check are relative to this container instead of the page viewport.
   * Useful for entry effects inside scrollable popups/modals.
   */
  root?: React.RefObject<HTMLElement | null>;
}

interface UseEntryEffectReturn {
  ref: React.RefObject<HTMLDivElement>;
  isVisible: boolean;
}

/**
 * How far the element's top must travel into the container before it reveals,
 * as a fraction of the container's height. At 0.15 a section starts animating
 * once its top passes 85% of the way down the viewport — i.e. as soon as it is
 * comfortably in view, which is what a scroll reveal is for.
 */
const REVEAL_VIEWPORT_FRACTION = 0.15;

function ugObserverInit(containerHeight: number, translateY: number): IntersectionObserverInit {
  const inset = Math.round(containerHeight * REVEAL_VIEWPORT_FRACTION) - translateY;
  return { threshold: 0, rootMargin: `0px 0px ${-inset}px 0px` };
}

function legacyObserverInit(
  elementHeight: number,
  containerHeight: number,
  translateY: number
): IntersectionObserverInit {
  const threshold = elementHeight > 0 ? Math.min(1, containerHeight / (2 * elementHeight)) : 0;
  const rootMargin = translateY > 0 ? `0px 0px -${translateY}px 0px` : '0px';
  return { threshold, rootMargin };
}

/**
 * Reads the current translateY from the element's computed transform matrix.
 * A positive value means the element is visually shifted downward from its
 * layout box, so the IO trigger must be delayed by shrinking the root's
 * bottom margin. Returns 0 when no transform is applied (e.g. the fade variant).
 */
function getTranslateY(element: HTMLElement): number {
  const { transform } = getComputedStyle(element);
  if (!transform || transform === 'none') return 0;
  const match = transform.match(/matrix.*\((.+)\)/);
  if (!match) return 0;
  const values = match[1].split(',').map(Number);
  return values.length === 6 ? values[5] : values[13] || 0;
}

/** Layout box overlaps the given bounds (or the viewport when no root). */
function layoutOverlapsBounds(el: HTMLElement, root?: HTMLElement | null): boolean {
  const r = el.getBoundingClientRect();
  if (root) {
    const b = root.getBoundingClientRect();
    return r.bottom > b.top && r.right > b.left && r.top < b.bottom && r.left < b.right;
  }
  return r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
}

/**
 * Scroll-triggered entry effect hook.
 *
 * Observes a DOM element and sets `isVisible` to `true` once it enters the
 * viewport or a given scroll container (one-shot -- the observer disconnects
 * after the first trigger).
 *
 * The trigger point is brand-dependent, deliberately:
 * - UG fires when the element's top crosses a line 85% down the container,
 *   independent of how tall the element is (`ugObserverInit`).
 * - Gallet and Breitling keep the original size-adaptive ratio — small elements
 *   fire when fully visible, tall ones when the top reaches the container
 *   centre (`legacyObserverInit`, which documents why it was not changed).
 *
 * When `root` is provided, the observer and overlap check are scoped to that
 * container so that entry effects inside scrollable popups fire as the user
 * scrolls the popup — not when the element enters the page viewport.
 *
 * If the observer reports no intersection (e.g. `intersectionRatio: 0` when
 * overflow clips translated content inside a popup) but the element's
 * **layout** rect still overlaps the bounds, we reveal anyway so the
 * CSS transition can run.
 */
export const useEntryEffect = ({
  enabled = true,
  root,
}: UseEntryEffectOptions = {}): UseEntryEffectReturn => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setIsVisible(true);
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const element = ref.current;
    if (!element) return;

    const rootElement = root?.current ?? null;

    let rafId: number | undefined;
    let done = false;
    const containerHeight = rootElement ? rootElement.clientHeight : window.innerHeight;
    const translateY = getTranslateY(element);
    // `BRAND` is inlined at build time, so the branch not taken is eliminated
    // from each brand's bundle.
    const observerInit =
      BRAND === BRAND_ENUM.Ug
        ? ugObserverInit(containerHeight, translateY)
        : legacyObserverInit(element.getBoundingClientRect().height, containerHeight, translateY);

    const observer = new IntersectionObserver(
      ([entry]) => {
        const intersects =
          entry.isIntersecting ||
          entry.intersectionRatio > 0 ||
          layoutOverlapsBounds(element, rootElement);
        if (!intersects || done) return;
        done = true;
        observer.disconnect();
        rafId = requestAnimationFrame(() => {
          setIsVisible(true);
        });
      },
      { root: rootElement, ...observerInit }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      if (rafId !== undefined) cancelAnimationFrame(rafId);
    };
  }, [enabled, root]);

  return { ref, isVisible };
};
